import type { TutorialStep } from "./types.ts";

/**
 * 「カスタム項目の設定と表示」チュートリアルのステップ定義。
 *
 * 「全体設定 → カスタム項目タブで追加（テキスト型・選択肢型）→ タスクの詳細パネルで
 * 入力 → タスクカードに表示」の流れを体験する。
 * カスタム項目はプロジェクトごとに管理される点がステータス・タグと異なる。
 *
 * このチュートリアルでは練習用にプロジェクトとタスクを 1 件ずつ用意済み。
 * targetKey は SandboxScreen 側の data-tut 属性と対応する。
 */
export const CUSTOM_TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "welcome",
    targetKey: null,
    placement: "center",
    title: "カスタム項目を学ぶ",
    body:
      "ここでは「カスタム項目」を体験します。\n" +
      "カスタム項目は、標準にない独自の入力欄をタスクに追加できる機能です。「テキスト型（自由入力）」と「選択肢型（候補から選ぶ）」の2種類があります。\n" +
      "「全体設定で追加 → 詳細画面で入力 → タスクカードに表示」の流れを進めます。練習用のプロジェクトとタスクは用意済みです。",
  },

  {
    id: "open-settings",
    targetKey: "settings-button",
    placement: "bottom",
    title: "① 全体設定を開く",
    body: "画面上部のツールバーにある歯車「全体設定」ボタンを押して、設定画面を開きましょう。",
    expectAction: "open-settings",
  },
  {
    id: "open-custom-tab",
    targetKey: "settings-tab-custom",
    placement: "bottom",
    title: "② カスタム項目タブを開く",
    body: "上部の「カスタム項目」タブをクリックしてください。",
    expectAction: "open-custom-tab",
  },
  {
    id: "add-custom-text",
    targetKey: "settings-add-custom",
    placement: "top",
    title: "③ テキスト型を追加する",
    body:
      "まずは自由入力の「テキスト型」を作ります。\n" +
      "項目名（例：「見積金額」）を入力し、入力形式で「テキスト」を選んで「追加」を押しましょう。",
    expectAction: "add-custom-text",
  },
  {
    id: "add-custom-select",
    targetKey: "settings-add-custom",
    placement: "top",
    title: "④ 選択肢型を追加する",
    body:
      "次に候補から選ぶ「選択肢型」を作ります。\n" +
      "項目名（例：「カテゴリ」）を入力し、入力形式で「選択肢」を選択。表示された欄に候補（例：「開発」「営業」）を入力し、「＋ 選択肢を追加」で増やせます。入力できたら「追加」を押しましょう。",
    expectAction: "add-custom-select",
  },
  {
    id: "close-settings",
    targetKey: "settings-close",
    placement: "left",
    title: "設定を閉じて確認する",
    body: "2種類のカスタム項目が追加できました。「閉じる」を押して、タスク側で反映を確認しましょう。",
    expectAction: "close-settings",
  },
  {
    id: "select-task",
    targetKey: "task-row",
    placement: "bottom",
    title: "⑤ タスクの詳細を開く",
    body: "タスクの行をクリックして、右側の詳細パネルを開きましょう。",
    expectAction: "select-task",
  },
  {
    id: "confirm-custom",
    targetKey: "detail-custom",
    placement: "left",
    title: "詳細画面での見え方を確認する",
    body:
      "詳細パネルのカスタム項目欄に、追加した2項目が表示されています。\n" +
      "「テキスト型」は自由に文字を入力できる入力欄、「選択肢型」は候補から選ぶドロップダウンとして表示されます。値を入れて違いを確かめ、「次へ」を押してください。",
  },

  {
    id: "open-display",
    targetKey: "custom-display-button",
    placement: "bottom",
    title: "⑥ タスクカードに表示する",
    body:
      "カスタム項目はタスクカード（リスト形式）にも表示できます。\n" +
      "プロジェクト名の右にある「表示項目の変更」ボタンを押しましょう。",
    expectAction: "open-custom-display",
  },
  {
    id: "save-display",
    targetKey: "custom-display-dialog",
    placement: "right",
    title: "表示する項目を選ぶ",
    body:
      "タスクカードに表示したいカスタム項目にチェックを入れます。▲▼で並び順も変えられます（1行に2項目まで表示）。\n" +
      "選んだら「保存する」を押してください。",
    expectAction: "save-custom-display",
  },
  {
    id: "confirm-card",
    targetKey: "task-row",
    placement: "bottom",
    title: "カードに表示されました",
    body:
      "タスクカードの「担当・期限」の下に、選んだカスタム項目が表示されました。\n" +
      "この表示設定はプロジェクトごとに保存されます。確認できたら「次へ」を押してください。",
  },

  {
    id: "done",
    targetKey: null,
    placement: "center",
    title: "お疲れさまでした！",
    body:
      "カスタム項目は「全体設定」のカスタム項目タブから、テキスト型・選択肢型を選んで追加できます。詳細画面で値を入力でき、「表示項目の変更」からタスクカードへの表示も設定できます。\n" +
      "ステータス・タグがルーム全体で共通なのに対し、カスタム項目はプロジェクトごとに管理されます。\n" +
      "「完了」を押すとチュートリアルを終了します。",
  },
];
